import type { CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';

import { T } from '../styles/tokens';

const plans = [
  { code: 'p30', days: 30, price: 150, perDay: '5.00', label: '30 дней' },
  { code: 'p90', days: 90, price: 400, perDay: '4.44', label: '90 дней', popular: true },
  { code: 'p180', days: 180, price: 700, perDay: '3.89', label: '180 дней' },
  { code: 'p365', days: 365, price: 1500, perDay: '4.11', label: '365 дней' },
];

export default function Landing() {
  const nav = useNavigate();

  return (
    <div style={s.root}>
      <div style={s.noise} />

      {/* NAV */}
      <nav style={s.nav}>
        <div style={s.navInner}>
          <div style={s.logo}>
            <span style={s.logoBox}>VS</span>
            <span style={s.logoName}>VPN Saler</span>
          </div>
          <div style={s.navRight}>
            <button style={s.navLink} onClick={() => document.getElementById('lp-pricing')?.scrollIntoView({ behavior: 'smooth' })}>
              Тарифы
            </button>
            <button style={s.navLink} onClick={() => document.getElementById('lp-proto')?.scrollIntoView({ behavior: 'smooth' })}>
              Протоколы
            </button>
            <div style={s.navDivider} />
            <button style={s.navSignIn} onClick={() => nav('/auth')}>Войти</button>
            <button style={s.navCta} onClick={() => nav('/auth?tab=register')}>Начать →</button>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section style={s.hero}>
        <div style={s.heroCont}>
          <div style={s.heroMeta}>
            <span style={s.heroPill}>7 дней бесплатно</span>
            <span style={s.heroMetaDot} />
            <span style={s.heroMetaText}>WireGuard · VLESS Reality</span>
          </div>
          <h1 style={s.heroTitle}>
            Свободный
            <br />
            <span style={s.heroAccent}>интернет</span>
            <br />
            без компромиссов
          </h1>
          <p style={s.heroSub}>
            VPN с автоматической выдачей конфигов прямо в Telegram.
            <br />
            Два протокола, безлимит, несколько устройств.
          </p>
          <div style={s.heroActions}>
            <button style={s.ctaBtn} onClick={() => nav('/auth?tab=register')}>Попробовать бесплатно</button>
            <button style={s.secondBtn} onClick={() => nav('/auth')}>Войти в кабинет</button>
          </div>
          <div style={s.heroStats}>
            {[
              ['WireGuard + VLESS', 'Протоколы'],
              ['Безлимит', 'Трафик'],
              ['DE', 'Сервер'],
              ['24/7', 'Поддержка'],
            ].map(([v, l]) => (
              <div key={l} style={s.stat}>
                <div style={s.statVal}>{v}</div>
                <div style={s.statLabel}>{l}</div>
              </div>
            ))}
          </div>
        </div>
        <div style={s.heroWordmark}>VPN</div>
      </section>

      {/* PROTOCOLS */}
      <section style={s.protoSection} id="lp-proto">
        <div style={s.sectionCont}>
          <div style={s.sectionTag}>// протоколы</div>
          <h2 style={s.sectionTitle}>
            Два протокола.
            <br />
            Один сервис.
          </h2>
        </div>
        <div style={s.protoBlocks}>
          {[
            {
              id: 'wireguard',
              name: 'WireGuard',
              tag: 'Скорость',
              desc: 'Минимальные накладные расходы, максимальная пропускная способность. Идеален для стриминга 4K, онлайн-игр, видеозвонков.',
              points: ['UDP, порт 51820', 'Конфиг .conf + QR', '10 мс средний пинг', 'iOS · Android · Win · Mac'],
              color: T.accent,
            },
            {
              id: 'vless',
              name: 'VLESS Reality',
              tag: 'Обход DPI',
              desc: 'XTLS-Vision flow, маскировка под HTTPS. Трафик неотличим от обычного браузера. Эффективно против блокировок РФ.',
              points: ['TCP · 443 · Reality SNI', 'vless:// ссылка + QR', 'Германия, DE', 'Streisand · v2rayNG · Hiddify'],
              color: T.success,
            },
          ].map((p, i) => (
            <div key={p.id} style={{ ...s.protoBlock, ...(i === 1 ? s.protoBlockAlt : {}) }}>
              <div style={s.protoBlockInner}>
                <div style={s.protoBlockLeft}>
                  <div style={{ ...s.protoTag, color: p.color, borderColor: p.color }}>{p.tag}</div>
                  <div style={s.protoName}>{p.name}</div>
                  <p style={s.protoDesc}>{p.desc}</p>
                </div>
                <div style={s.protoBlockRight}>
                  {p.points.map((pt) => (
                    <div key={pt} style={s.protoPoint}>
                      <span style={{ ...s.protoDash, color: p.color }}>—</span>
                      <span>{pt}</span>
                    </div>
                  ))}
                  <button
                    style={{ ...s.ctaBtn, marginTop: 24, background: p.color }}
                    onClick={() => nav('/auth?tab=register')}
                  >
                    Подключить
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* PRICING */}
      <section style={s.pricingSection} id="lp-pricing">
        <div style={s.sectionCont}>
          <div style={s.sectionTag}>// тарифы</div>
          <h2 style={s.sectionTitle}>Прозрачные цены</h2>
          <p style={s.sectionSub}>Все тарифы: оба протокола, безлимит, несколько устройств, поддержка 24/7</p>
        </div>
        <div style={s.pricingTable}>
          <div style={s.pricingHead}>
            <div style={s.pth}>Период</div>
            <div style={s.pth}>Цена</div>
            <div style={s.pth}>В день</div>
            <div style={s.pth}>Включено</div>
            <div style={s.pth} />
          </div>
          {plans.map((plan) => (
            <div key={plan.code} style={{ ...s.pricingRow, ...(plan.popular ? s.pricingRowHot : {}) }}>
              <div style={s.ptd}>
                <span style={s.planPeriod}>{plan.label}</span>
                {plan.popular && <span style={s.hotBadge}>популярный</span>}
              </div>
              <div style={s.ptd}>
                <span style={s.planPrice}>{plan.price}</span>
                <span style={s.planCur}> ₽</span>
              </div>
              <div style={{ ...s.ptd, ...s.planPerDay }}>{plan.perDay} ₽</div>
              <div style={s.ptd}>
                <span style={s.included}>Оба протокола · Безлимит · Несколько устройств</span>
              </div>
              <div style={s.ptd}>
                <button
                  style={plan.popular ? s.ctaBtn : s.outlineSmBtn}
                  onClick={() => nav('/auth?tab=register')}
                >
                  Выбрать
                </button>
              </div>
            </div>
          ))}
        </div>

        <div style={s.trialStrip}>
          <div style={s.trialLeft}>
            <span style={s.trialBig}>7 дней бесплатно</span>
            <span style={s.trialSub}>&nbsp;— без карты, оба протокола сразу</span>
          </div>
          <button style={s.ctaBtn} onClick={() => nav('/auth?tab=register')}>
            Начать бесплатно →
          </button>
        </div>
      </section>

      {/* HOW */}
      <section style={s.howSection}>
        <div style={s.sectionCont}>
          <div style={s.sectionTag}>// как это работает</div>
          <div style={s.steps}>
            {[
              ['01', 'Регистрация', 'Создайте аккаунт или войдите через Telegram за 30 секунд'],
              ['02', 'Выберите тариф', 'Выберите протокол и тариф. Оплатите через Т-Банк, ЮKassa или TG Pay'],
              ['03', 'Получите конфиг', 'Конфигурация автоматически выдаётся в Telegram-бот и в личный кабинет'],
              ['04', 'Подключайтесь', 'Отсканируйте QR или импортируйте файл. Включите тумблер — готово'],
            ].map(([num, title, desc]) => (
              <div key={num} style={s.step}>
                <div style={s.stepNum}>{num}</div>
                <div style={s.stepTitle}>{title}</div>
                <div style={s.stepDesc}>{desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer style={s.footer}>
        <div style={s.footerInner}>
          <div style={s.logo}>
            <span style={s.logoBox}>VS</span>
            <span style={s.logoName}>VPN Saler</span>
          </div>
          <div style={s.footerLinks}>
            <button style={s.navLink} onClick={() => nav('/instructions')}>Инструкции</button>
            <button style={s.navLink} onClick={() => nav('/support')}>Поддержка</button>
            <a href="https://t.me/vpnsalerpy_bot" style={{ ...s.navLink, textDecoration: 'none' }}>
              Telegram
            </a>
          </div>
          <div style={s.footerCopy}>© 2026 VPN Saler</div>
        </div>
      </footer>
    </div>
  );
}

const s: Record<string, CSSProperties> = {
  root: { background: T.bg, minHeight: '100vh', color: T.text, fontFamily: 'DM Sans, sans-serif', position: 'relative', overflow: 'hidden' },
  noise: {
    position: 'fixed',
    inset: 0,
    pointerEvents: 'none',
    zIndex: 0,
    opacity: 0.025,
    backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
  },
  nav: { position: 'sticky', top: 0, zIndex: 100, background: 'rgba(9,9,11,0.9)', backdropFilter: 'blur(16px)', borderBottom: `1px solid ${T.border}` },
  navInner: { maxWidth: 1200, margin: '0 auto', padding: '0 32px', height: 60, display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  logo: { display: 'flex', alignItems: 'center', gap: 10, cursor: 'default' },
  logoBox: { width: 30, height: 30, background: T.accent, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, color: '#000', letterSpacing: '0.02em', fontFamily: 'DM Mono, monospace' },
  logoName: { fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 16, letterSpacing: '-0.02em', color: T.text },
  navRight: { display: 'flex', alignItems: 'center', gap: 4 },
  navLink: { background: 'none', border: 'none', color: T.textSec, fontFamily: 'DM Sans, sans-serif', fontSize: 13, fontWeight: 500, cursor: 'pointer', padding: '6px 12px', borderRadius: 3 },
  navDivider: { width: 1, height: 18, background: T.border, margin: '0 8px' },
  navSignIn: { background: 'none', border: 'none', color: T.text, fontFamily: 'DM Sans, sans-serif', fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: '6px 14px', borderRadius: 3 },
  navCta: { background: T.accent, border: 'none', color: '#000', fontFamily: 'DM Sans, sans-serif', fontSize: 13, fontWeight: 700, cursor: 'pointer', padding: '8px 18px', borderRadius: 4 },
  hero: { position: 'relative', minHeight: '92vh', display: 'flex', alignItems: 'center', borderBottom: `1px solid ${T.border}`, overflow: 'hidden' },
  heroCont: { maxWidth: 1200, margin: '0 auto', padding: '80px 32px', position: 'relative', zIndex: 2, flex: 1 },
  heroWordmark: { position: 'absolute', right: -40, top: '50%', transform: 'translateY(-50%)', fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 'clamp(160px, 22vw, 340px)', color: 'transparent', WebkitTextStroke: `1px ${T.border}`, userSelect: 'none', letterSpacing: '-0.05em', lineHeight: 1, zIndex: 1, pointerEvents: 'none' },
  heroMeta: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 28 },
  heroPill: { fontFamily: 'DM Mono, monospace', fontSize: 11, fontWeight: 500, color: T.accent, background: T.accentSoft, border: `1px solid ${T.accentMid}`, padding: '4px 10px', borderRadius: 2, letterSpacing: '0.06em', textTransform: 'uppercase' },
  heroMetaDot: { width: 3, height: 3, borderRadius: '50%', background: T.textMuted },
  heroMetaText: { fontFamily: 'DM Mono, monospace', fontSize: 11, color: T.textMuted, letterSpacing: '0.04em' },
  heroTitle: { fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 'clamp(42px, 7vw, 96px)', lineHeight: 1.0, letterSpacing: '-0.04em', margin: '0 0 24px', color: T.text },
  heroAccent: { color: T.accent },
  heroSub: { fontSize: 16, color: T.textSec, lineHeight: 1.7, maxWidth: 480, margin: '0 0 40px' },
  heroActions: { display: 'flex', gap: 12, marginBottom: 56, flexWrap: 'wrap' },
  ctaBtn: { background: T.accent, border: 'none', borderRadius: 4, padding: '13px 28px', color: '#000', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', letterSpacing: '0.01em' },
  secondBtn: { background: 'transparent', border: `1px solid ${T.border}`, borderRadius: 4, padding: '12px 24px', color: T.text, fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  outlineSmBtn: { background: 'transparent', border: `1px solid ${T.border}`, borderRadius: 3, padding: '9px 18px', color: T.text, fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  heroStats: { display: 'flex', gap: 0, borderTop: `1px solid ${T.border}`, paddingTop: 28, maxWidth: 480 },
  stat: { flex: 1, paddingRight: 24, borderRight: `1px solid ${T.border}`, marginRight: 24 },
  statVal: { fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 15, color: T.text, marginBottom: 3, letterSpacing: '-0.01em' },
  statLabel: { fontFamily: 'DM Mono, monospace', fontSize: 10, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em' },
  sectionCont: { maxWidth: 1200, margin: '0 auto', padding: '0 32px' },
  sectionTag: { fontFamily: 'DM Mono, monospace', fontSize: 11, color: T.accent, letterSpacing: '0.08em', marginBottom: 16, textTransform: 'uppercase' },
  sectionTitle: { fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 'clamp(28px, 4vw, 52px)', letterSpacing: '-0.03em', lineHeight: 1.1, margin: '0 0 16px', color: T.text },
  sectionSub: { fontSize: 15, color: T.textSec, lineHeight: 1.6, marginBottom: 0 },
  protoSection: { padding: '100px 0 0', borderBottom: `1px solid ${T.border}` },
  protoBlocks: { marginTop: 64 },
  protoBlock: { borderTop: `1px solid ${T.border}`, padding: '0' },
  protoBlockAlt: { background: T.surface },
  protoBlockInner: { maxWidth: 1200, margin: '0 auto', padding: '60px 32px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 80 },
  protoBlockLeft: {},
  protoBlockRight: { paddingTop: 8 },
  protoTag: { display: 'inline-block', fontFamily: 'DM Mono, monospace', fontSize: 10, fontWeight: 500, padding: '3px 8px', border: '1px solid', borderRadius: 2, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 16 },
  protoName: { fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 'clamp(24px, 3vw, 40px)', letterSpacing: '-0.03em', marginBottom: 16, color: T.text },
  protoDesc: { fontSize: 15, color: T.textSec, lineHeight: 1.7, maxWidth: 380 },
  protoPoint: { display: 'flex', gap: 12, fontSize: 14, color: T.textSec, marginBottom: 10, fontWeight: 500, alignItems: 'center' },
  protoDash: { fontFamily: 'DM Mono, monospace', fontSize: 14, fontWeight: 700, flexShrink: 0 },
  pricingSection: { padding: '100px 0' },
  pricingTable: { maxWidth: 1200, margin: '40px auto 0', padding: '0 32px' },
  pricingHead: { display: 'grid', gridTemplateColumns: '180px 120px 90px 1fr 130px', padding: '8px 0', borderBottom: `1px solid ${T.border}`, marginBottom: 0 },
  pth: { fontFamily: 'DM Mono, monospace', fontSize: 10, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em', padding: '6px 0' },
  pricingRow: { display: 'grid', gridTemplateColumns: '180px 120px 90px 1fr 130px', alignItems: 'center', borderBottom: `1px solid ${T.border}`, padding: '20px 0', transition: 'background 0.15s' },
  pricingRowHot: { background: T.accentSoft },
  ptd: { padding: '0 12px 0 0', fontSize: 14, color: T.textSec },
  planPeriod: { fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 16, color: T.text, letterSpacing: '-0.02em' },
  hotBadge: { marginLeft: 10, fontFamily: 'DM Mono, monospace', fontSize: 9, fontWeight: 600, color: T.accent, background: T.accentSoft, border: `1px solid ${T.accentMid}`, padding: '2px 7px', borderRadius: 2, textTransform: 'uppercase', letterSpacing: '0.08em', verticalAlign: 'middle' },
  planPrice: { fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 22, color: T.text, letterSpacing: '-0.03em' },
  planCur: { fontSize: 14, color: T.textSec, fontWeight: 400 },
  planPerDay: { fontFamily: 'DM Mono, monospace', fontSize: 12, color: T.textMuted },
  included: { fontSize: 12, color: T.textMuted },
  trialStrip: { maxWidth: 1200, margin: '0 auto', padding: '28px 32px', borderTop: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16, marginTop: 0 },
  trialLeft: { fontSize: 15 },
  trialBig: { fontFamily: 'Syne, sans-serif', fontWeight: 700, color: T.accent, fontSize: 18 },
  trialSub: { color: T.textSec },
  howSection: { padding: '100px 0', borderTop: `1px solid ${T.border}` },
  steps: { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 0, marginTop: 48, borderTop: `1px solid ${T.border}` },
  step: { padding: '32px 28px 32px 0', borderRight: `1px solid ${T.border}`, paddingRight: 28 },
  stepNum: { fontFamily: 'DM Mono, monospace', fontSize: 11, color: T.accent, letterSpacing: '0.1em', marginBottom: 14 },
  stepTitle: { fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 17, letterSpacing: '-0.02em', marginBottom: 10, color: T.text },
  stepDesc: { fontSize: 13, color: T.textSec, lineHeight: 1.6 },
  footer: { borderTop: `1px solid ${T.border}`, padding: '28px 0' },
  footerInner: { maxWidth: 1200, margin: '0 auto', padding: '0 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 },
  footerLinks: { display: 'flex', gap: 4 },
  footerCopy: { fontFamily: 'DM Mono, monospace', fontSize: 11, color: T.textMuted },
};
